import * as React from 'react'
import * as cn from 'classnames'
import * as styles from './Packshot.module.scss'
import { TwinHeroTextOverlay } from '../TextOverlay'
import Smile from './Smile'
import { Logo, LogoOptions } from '@amzn/dvui/dist/Logo'
import { Spinner } from "@amzn/dvui/dist/Spinner";

import BasicImage from './BasicImage'
import PlayButtonContainer from './PlayButtonContainer'
import PackshotContainer from './PackshotContainer'
import * as csm from '../../../../../utils/csm'
import { FeatureSwitches } from '../../../../../containers/Storefront';
import { StorefrontItemPlaybackAction, StorefrontLink, StorefrontItemImage, safeBool, EntitlementCueObject, StationData } from 'DVWebNodeUtils';
import { CollectionType } from '../../../Collection'
import { EntitlementCue } from '../../../../../../../EntitlementCue/EntitlementCue'
import { ChannelLogo } from './ChannelLogo'

const BASELINE_PACKSHOT_IMG_WIDTH = 356
const BASELINE_TWIN_HERO_IMG_WIDTH = 920
const BASELINE_SQUARE_IMG_WIDTH = 200
const BASELINE_PORTRAIT_IMG_WIDTH = 184;
const BASELINE_BOXART_IMG_WIDTH = 200;

interface PlaceholderProps {
  width: number
  height: number
  omitPlaceholderGraphic?: boolean
}

const Placeholder = ({ width, height, omitPlaceholderGraphic }: PlaceholderProps) => (
  <><Smile width={width} height={height} showImage={!omitPlaceholderGraphic} /></>
)

const LoadingPlaceholder = (props: PlaceholderProps) => (
  <>
    <Placeholder {...props} />
    <div className={styles.Loading}><Spinner fillHeight /></div>
  </>
)

export interface PackshotProps {
  titleID: string
  isIdPlayable: boolean
  playbackAction: StorefrontItemPlaybackAction
  collectionType: CollectionType
  link: StorefrontLink
  image: StorefrontItemImage
  isLazy: boolean
  isLive: boolean
  isRounded: boolean
  itemType: string
  hasPrimeSash: boolean
  showPlayButton: boolean
  canLivePlayback: boolean
  featureSwitches: FeatureSwitches
  title: string
  alternateText: string
  isVerticallyVisible?: boolean
  playButtonClassName?: string
  hoverIsExpanded: boolean
  entitlementCue?: EntitlementCueObject
  station?: StationData
}

interface PackshotState {
  imageHasError: boolean
  isImageLoading: boolean
}

class Packshot extends React.PureComponent<PackshotProps, PackshotState> {
  playButtonRef: React.RefObject<HTMLAnchorElement>
  packshotRef: React.RefObject<HTMLImageElement>
  state: PackshotState

  constructor(props: PackshotProps) {
    super(props)
    this.state = {
      isImageLoading: true,
      imageHasError: false
    }

    // the click then enrich play button flow requires us to "click" on the play button,
    // to ensure that the shared player code works in exactly the same ways as otherwise
    // if we have no playback, we click on the packshot to go to detail page
    this.playButtonRef = React.createRef()
    this.packshotRef = React.createRef()
  }

  handleImageError(titleID: string) {
    return (error: React.SyntheticEvent<HTMLImageElement>) => {
      const errorId = 'dvWebClientImgLoadFailure'
      csm.count(errorId)
      csm.logError(error, `[${errorId}] the image for the following titleID is missing: ${titleID}`)
      this.setState({ imageHasError: true, isImageLoading: false })
    }
  }

  handleImageLoad = () => {
    this.setState({ isImageLoading: false })
  }

  getDimensions() {
    switch (this.props.collectionType) {
      case 'TwinHero':
        return {
          width: 23,
          height: 10,
          defaultWidth: BASELINE_TWIN_HERO_IMG_WIDTH
        }
      case 'SquareCarousel':
        return {
          width: 1,
          height: 1,
          defaultWidth: BASELINE_SQUARE_IMG_WIDTH
        }
      case 'PortraitCarousel':
        return {
          width: 3,
          height: 4,
          defaultWidth: BASELINE_PORTRAIT_IMG_WIDTH
        }
      case 'BoxArtCarousel':
        return {
          width: 3,
          height: 4,
          defaultWidth: BASELINE_BOXART_IMG_WIDTH
        }
      default:
        return {
          width: 16,
          height: 9,
          defaultWidth: BASELINE_PACKSHOT_IMG_WIDTH
        }
    }
  }

  render() {
    const { image, isLazy,
      playbackAction, hasPrimeSash, itemType,
      isLive, titleID, isRounded, featureSwitches } = this.props
    const hasImage = !isLazy && image && image.url
    const dimensions = this.getDimensions()
    const isLabelType = itemType === 'Label'
    const isLoadingType = itemType === 'Loading'

    const isPortrait = this.props.collectionType === "PortraitCarousel"
    const isBoxArt = this.props.collectionType === "BoxArtCarousel"
    const classNames = cn(
      {
        [styles.isRounded]: isRounded,
        [styles.PlaceholderBackground]: !isPortrait,
        [styles.PortraitPackshot]: isPortrait,
        [styles.BoxArtPackshot]: isBoxArt
      },
      styles.Packshot,
      'tst-packshot'
    )

    return (
      <PackshotContainer {...this.props}
        classNames={classNames}
      >
        {this.props.entitlementCue && <EntitlementCue
          {...this.props.entitlementCue}
          increaseZIndex={this.props.hoverIsExpanded}
          hoverIsExpanded={this.props.hoverIsExpanded}
        /> || <></>}
        {isLoadingType ?
          <LoadingPlaceholder {...dimensions} omitPlaceholderGraphic={true} />
          :
          <Placeholder {...dimensions} omitPlaceholderGraphic={isLabelType} />
        }
        {image && hasImage && !this.state.imageHasError &&
          (<div className={styles.Image}>
            <BasicImage
              key={image.url}
              url={image.url}
              alternateText={image.alternateText}
              onError={this.handleImageError(titleID)}
              onLoad={this.handleImageLoad}
              defaultWidth={dimensions.defaultWidth}
              legacy={featureSwitches.isIEorLegacyEdge}
              className={styles.packshotImage}
            />
          </div>)
          || <></>
        }
        {hasPrimeSash && <Logo use={LogoOptions.primeSash} className={cn(styles.Sash, { [styles.overlaidSash]: this.props.hoverIsExpanded })}></Logo> || <></>}
        {(isLive || safeBool(this.props.showPlayButton)) && Boolean(playbackAction) &&
          <PlayButtonContainer
            playbackAction={playbackAction}
            isLive={isLive}
            canLivePlayback={this.props.canLivePlayback}
            playButtonClassName={this.props.playButtonClassName}
            linkRef={this.playButtonRef}
            shouldFadeIn={this.props.featureSwitches && this.props.featureSwitches.lazyloadPB}
          /> || <></>}
        {this.props.station && this.props.station.logo && <ChannelLogo url={this.props.station.logo} name={this.props.station.name} /> || <></>}
        {this.props.collectionType === "TwinHero" &&
          Boolean(this.props.title) && !isLabelType && !isLoadingType &&
          <TwinHeroTextOverlay title={this.props.title} text={this.props.alternateText} /> || <></>}
        {this.props.collectionType === "PortraitCarousel" && this.props.title &&
          <div className={styles.PortraitLabelWrapper}>
            <p className={styles.PortraitLabel}>{this.props.title}</p>
          </div> || <></>}
      </PackshotContainer>
    )
  }
}

export default Packshot
